/**
 * CompactModePanel - Collapsed sidebar with icon buttons and hover flyouts
 *
 * Renders Site/System mode icons with hover menus containing the same
 * controls as the expanded panel.
 */

import React from 'react';
import {
  Home as HomeIcon,
  Grid3x3,
  AlertTriangle,
  ChevronRight,
} from 'lucide-react';

// Import control components
import { RoofControls } from '../../aurora/RoofControls';
import { ObstructionControls } from '../../aurora/ObstructionControls';
import { ModuleControls } from '../../aurora/ModuleControls';

interface CompactModePanelProps {
  // Mode
  siteSystemMode: 'site' | 'system';
  onModeChange: (mode: 'site' | 'system') => void;

  // Card collapse states (for icon highlighting)
  roofCardCollapsed: boolean;
  setRoofCardCollapsed: (v: boolean) => void;
  obstructionCardCollapsed: boolean;
  setObstructionCardCollapsed: (v: boolean) => void;
  moduleCardCollapsed: boolean;
  setModuleCardCollapsed: (v: boolean) => void;

  // Pass-through props for controls
  roofControlsProps: React.ComponentProps<typeof RoofControls>;
  obstructionControlsProps: React.ComponentProps<typeof ObstructionControls>;
  moduleControlsProps: React.ComponentProps<typeof ModuleControls>;
}

export function CompactModePanel({
  siteSystemMode,
  onModeChange,
  roofCardCollapsed,
  setRoofCardCollapsed,
  obstructionCardCollapsed,
  setObstructionCardCollapsed,
  moduleCardCollapsed,
  setModuleCardCollapsed,
  roofControlsProps,
  obstructionControlsProps,
  moduleControlsProps,
}: CompactModePanelProps) {
  return (
    <div className="p-2 flex flex-col gap-2">
      {/* Mode toggle button */}
      <button
        onClick={() => onModeChange(siteSystemMode === 'site' ? 'system' : 'site')}
        className="flex flex-col items-center justify-center p-2 bg-gray-100 hover:bg-[#EBFD03] rounded-lg transition-colors cursor-pointer"
        title={`Current: ${siteSystemMode === 'site' ? 'Site' : 'System'} mode - Click to switch`}
      >
        {siteSystemMode === 'site' ? (
          <HomeIcon className="h-5 w-5 text-gray-700" />
        ) : (
          <Grid3x3 className="h-5 w-5 text-gray-700" />
        )}
        <div className="text-xs font-semibold text-gray-900 mt-1">
          {siteSystemMode === 'site' ? 'Site' : 'System'}
        </div>
      </button>

      {/* Site Mode Icons */}
      {siteSystemMode === 'site' && (
        <>
          {/* Roof Icon with hover menu */}
          <div className="group/roof relative">
            <button
              onClick={() => setRoofCardCollapsed(!roofCardCollapsed)}
              className={`flex items-center justify-center p-3 rounded-lg transition-colors cursor-pointer w-full ${
                !roofCardCollapsed ? 'bg-[#EBFD03]' : 'bg-gray-100 hover:bg-gray-200'
              }`}
              title="Roof Controls - Hover for options"
            >
              <HomeIcon className="h-6 w-6 text-gray-700" />
            </button>
            <ChevronRight className="h-4 w-4 text-gray-600 absolute right-0 top-1/2 -translate-y-1/2 opacity-0 group-hover/roof:opacity-100 transition-opacity pointer-events-none z-[10000]" />

            {/* Roof Controls Flyout */}
            <div
              className="fixed invisible group-hover/roof:visible opacity-0 group-hover/roof:opacity-100 hover:!visible hover:!opacity-100 transition-all duration-150"
              style={{ zIndex: 10000, left: '88px', top: '80px' }}
            >
              <div className="bg-white border-2 border-gray-300 rounded-lg shadow-xl p-4 min-w-[240px] max-h-[calc(100vh-120px)] overflow-y-auto">
                <div className="flex items-center gap-2 mb-4 pb-3 border-b-2 border-gray-200">
                  <HomeIcon className="h-5 w-5 text-gray-700" />
                  <h3 className="text-sm font-bold text-gray-900">Roof Controls</h3>
                </div>
                <RoofControls {...roofControlsProps} collapsed={false} />
              </div>
            </div>
          </div>

          {/* Obstruction Icon with hover menu */}
          <div className="group/obstruction relative">
            <button
              onClick={() => setObstructionCardCollapsed(!obstructionCardCollapsed)}
              className={`flex items-center justify-center p-3 rounded-lg transition-colors cursor-pointer w-full ${
                !obstructionCardCollapsed ? 'bg-[#EBFD03]' : 'bg-gray-100 hover:bg-gray-200'
              }`}
              title="Obstruction Controls - Hover for options"
            >
              <AlertTriangle className="h-6 w-6 text-gray-700" />
            </button>
            <ChevronRight className="h-4 w-4 text-gray-600 absolute right-0 top-1/2 -translate-y-1/2 opacity-0 group-hover/obstruction:opacity-100 transition-opacity pointer-events-none z-[10000]" />

            {/* Obstruction Controls Flyout */}
            <div
              className="fixed invisible group-hover/obstruction:visible opacity-0 group-hover/obstruction:opacity-100 hover:!visible hover:!opacity-100 transition-all duration-150"
              style={{ zIndex: 10000, left: '88px', top: '140px' }}
            >
              <div className="bg-white border-2 border-gray-300 rounded-lg shadow-xl p-4 min-w-[240px] max-h-[calc(100vh-180px)] overflow-y-auto">
                <div className="flex items-center gap-2 mb-4 pb-3 border-b-2 border-gray-200">
                  <AlertTriangle className="h-5 w-5 text-gray-700" />
                  <h3 className="text-sm font-bold text-gray-900">Obstruction Controls</h3>
                </div>
                <ObstructionControls {...obstructionControlsProps} collapsed={false} />
              </div>
            </div>
          </div>
        </>
      )}

      {/* System Mode Icons */}
      {siteSystemMode === 'system' && (
        <div className="group/modules relative">
          <button
            onClick={() => setModuleCardCollapsed(!moduleCardCollapsed)}
            className={`flex items-center justify-center p-3 rounded-lg transition-colors cursor-pointer w-full ${
              !moduleCardCollapsed ? 'bg-[#EBFD03]' : 'bg-gray-100 hover:bg-gray-200'
            }`}
            title="Module Controls - Hover for options"
          >
            <Grid3x3 className="h-6 w-6 text-gray-700" />
          </button>
          <ChevronRight className="h-4 w-4 text-gray-600 absolute right-0 top-1/2 -translate-y-1/2 opacity-0 group-hover/modules:opacity-100 transition-opacity pointer-events-none z-[10000]" />

          {/* Module Controls Flyout */}
          <div
            className="fixed invisible group-hover/modules:visible opacity-0 group-hover/modules:opacity-100 hover:!visible hover:!opacity-100 transition-all duration-150"
            style={{ zIndex: 10000, left: '88px', top: '80px' }}
          >
            <div className="bg-white border-2 border-gray-300 rounded-lg shadow-xl p-4 min-w-[285px] max-h-[calc(100vh-120px)] overflow-y-auto">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b-2 border-gray-200">
                <Grid3x3 className="h-5 w-5 text-gray-700" />
                <h3 className="text-sm font-bold text-gray-900">Module Controls</h3>
              </div>
              <ModuleControls {...moduleControlsProps} collapsed={false} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CompactModePanel;
